// Start of Selection
import React, {
  useRef,
  useEffect,
  Suspense,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Canvas, useFrame, useThree, GroupProps } from '@react-three/fiber';
import { Environment, Html, useGLTF } from '@react-three/drei';
import CustomShaderMaterial from 'three-custom-shader-material';
import * as CANNON from 'cannon-es';
import * as THREE from 'three';

const EARTH_SIZE = 0.53;

// For Earth spin
const EARTH_SPIN = 0.001;

// Define the custom shaders for the Earth
const earthVertexShader = `
      varying vec3 vPositionW;
      varying vec3 vNormalW;
    
      void main() {
        vPositionW = normalize(vec3(modelViewMatrix * vec4(position, 1.0)).xyz);
        vNormalW = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

const earthFragmentShader = `
      varying vec3 vPositionW;
      varying vec3 vNormalW;
      uniform float fresnelPower;
      uniform float fresnelStrength;
    
      void main() {
        float fresnelTerm = (1.0 - -min(dot(vPositionW, normalize(vNormalW)), 0.0));
        fresnelTerm = pow(fresnelTerm, fresnelPower);
        csm_DiffuseColor = mix(csm_DiffuseColor, vec4(0.0, 0.0, 1.0, 1.0), fresnelTerm * fresnelStrength);
      }
    `;

// Define the custom shaders for the puzzle pieces
const puzzleVertexShader = `
      varying vec2 vUv;
    
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

const puzzleFragmentShader = `
      varying vec2 vUv;
      uniform sampler2D map;
      uniform vec3 col;
      uniform float blink;
    
      void main() {
        vec2 mappedUV = vUv;
        float blinkOffset = mix(-.05, .54, blink);
        mappedUV.x = 0.5 * mappedUV.x + blinkOffset;
        mappedUV.y = 0.5 * mappedUV.y + 0.25;
        vec4 texelColor = texture2D(map, mappedUV);
        csm_DiffuseColor = vec4(texelColor.rgb * col, 1.0);
      }
    `;

// Define the colors for the puzzle pieces
const puzzleColors = [0xffcccc, 0xccccff, 0xffffcc, 0xffccff];

// Easing function for smooth zooming
const easeInOutQuad = (t: number) => {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
};

const IntroScreenModel = forwardRef((props: GroupProps, ref) => {
  const group = useRef<THREE.Group>(null!);
  const { nodes, materials } = useGLTF('./scene1.glb');

  // References to the meshes
  const earthRef = useRef<THREE.Mesh>(null!);
  const puzzleRefs = [
    useRef<THREE.Mesh>(null!),
    useRef<THREE.Mesh>(null!),
    useRef<THREE.Mesh>(null!),
    useRef<THREE.Mesh>(null!),
  ];

  // State for blink
  const blinkValues = useRef([0, 0, 0, 0]);

  // References for physics
  const physicsWorld = useRef(
    new CANNON.World({
      gravity: new CANNON.Vec3(0, 0, 0),
    })
  );

  const earthRotAmt = useRef<number>(0.0);

  const earthBody = useRef<CANNON.Body | null>(null);
  const puzzleBodies = useRef<CANNON.Body[]>([]);
  const originalPositions = useRef<THREE.Vector3[]>([]);

  const { camera, raycaster, mouse } = useThree();

  // Camera sway variables
  const swayOffset = useRef(new THREE.Vector3(0, 0, 0));
  const swayMag = 0.1;
  const originalCameraPosition = useRef(new THREE.Vector3());

  // Zoom animation variables
  const isZooming = useRef(false);
  const zoomStartPos = useRef(new THREE.Vector3());
  const zoomEndPos = useRef(new THREE.Vector3());
  const zoomProgress = useRef(0);
  const zoomDuration = 2; // seconds

  // Move camera closer to the model
  useEffect(() => {
    camera.position.set(0, 0, 1.55); // Adjust the camera position to be closer
    originalCameraPosition.current.copy(camera.position);
  }, [camera]);

  // Initialize physics bodies after GLTF is loaded
  useEffect(() => {
    const world = physicsWorld.current;
    world.broadphase = new CANNON.SAPBroadphase(world);

    // Create Earth physics body
    const earthMesh = earthRef.current;
    const earthShape = new CANNON.Sphere(EARTH_SIZE);
    const earthPhysBody = new CANNON.Body({
      mass: 1,
      position: new CANNON.Vec3(
        earthMesh.position.x,
        earthMesh.position.y,
        earthMesh.position.z
      ),
      shape: earthShape,
      angularDamping: 0.5,
      linearDamping: 1.0,
    });
    world.addBody(earthPhysBody);
    earthBody.current = earthPhysBody;

    // Create puzzle pieces physics bodies
    const tempPuzzleBodies: CANNON.Body[] = [];
    const tempOriginalPositions: THREE.Vector3[] = [];
    const tempOriginalQuaternions: THREE.Quaternion[] = [];

    puzzleRefs.forEach((ref) => {
      const mesh = ref.current;
      const box = new THREE.Box3().setFromObject(mesh);
      const size = new THREE.Vector3();
      box.getSize(size);
      const halfExtents = new CANNON.Vec3(size.x / 2, size.y / 2, size.z / 2);
      const shape = new CANNON.Box(halfExtents);

      // Convert THREE.js Euler rotation to Quaternion for CANNON
      const quaternion = new CANNON.Quaternion();
      quaternion.setFromEuler(
        mesh.rotation.x,
        mesh.rotation.y,
        mesh.rotation.z,
        'XYZ'
      );

      const physBody = new CANNON.Body({
        mass: 1,
        position: new CANNON.Vec3(
          mesh.position.x,
          mesh.position.y,
          mesh.position.z
        ),
        quaternion: quaternion,
        shape: shape,
        angularDamping: 0.8,
        linearDamping: 0.8,
      });
      world.addBody(physBody);
      tempPuzzleBodies.push(physBody);
      tempOriginalPositions.push(mesh.position.clone());
      tempOriginalQuaternions.push(mesh.quaternion.clone());
    });

    puzzleBodies.current = tempPuzzleBodies;
    originalPositions.current = tempOriginalPositions;
    // originalQuaternions.current = tempOriginalQuaternions;
  }, [nodes]);

  // Implement interaction logic
  useEffect(() => {
    const handleClick = () => {
      raycaster.setFromCamera(mouse, camera);

      // Check for intersection with puzzle pieces
      const intersectsPuzzles = raycaster.intersectObjects(
        puzzleRefs.map((ref) => ref.current)
      );

      if (intersectsPuzzles.length > 0) {
        const index = puzzleRefs.findIndex(
          (ref) => ref.current === intersectsPuzzles[0].object
        );
        if (index !== -1 && puzzleBodies.current[index]) {
          // Trigger blink effect
          blinkValues.current[index] = 1;

          // Apply random force to the puzzle piece
          const forceMagnitude = 50;
          const randomForce = new CANNON.Vec3(
            (Math.random() - 0.5) * forceMagnitude,
            (Math.random() - 0.5) * forceMagnitude,
            (Math.random() - 0.5) * forceMagnitude
          );
          puzzleBodies.current[index].applyForce(
            randomForce,
            puzzleBodies.current[index].position
          );
        }
      }

      // Check for intersection with Earth
      const intersectsEarth = raycaster.intersectObject(earthRef.current);
      if (intersectsEarth.length > 0 && earthBody.current) {
        // Increase Earth spin by applying a torque
        const torque = new CANNON.Vec3(0, 10, 0);
        earthBody.current.torque.vadd(torque, earthBody.current.torque);
      }
    };

    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [camera, raycaster, mouse]);

  // Expose zoom function to parent via ref
  useImperativeHandle(ref, () => ({
    zoomIntoEarth: (endDistance: number = -0.7) => {
      if (isZooming.current) return; // Prevent multiple zooms at the same time
      isZooming.current = true;
      zoomProgress.current = 0;

      // Set start and end positions
      zoomStartPos.current.copy(camera.position);

      const earthWorldPosition = new THREE.Vector3();
      earthRef.current.getWorldPosition(earthWorldPosition);

      const direction = new THREE.Vector3()
        .subVectors(earthWorldPosition, camera.position)
        .normalize();
      zoomEndPos.current
        .copy(earthWorldPosition)
        .add(direction.multiplyScalar(endDistance));
    },
  }));

  // Handle zoom animation
  useFrame((state, delta) => {
    const world = physicsWorld.current;
    world.step(delta);

    // Rotate Earth based on physics
    if (earthBody.current && earthRef.current) {
      earthRef.current.position.copy(
        earthBody.current.position as unknown as THREE.Vector3
      );
      earthRef.current.quaternion.copy(
        earthBody.current.quaternion as unknown as THREE.Quaternion
      );

      earthRotAmt.current += EARTH_SPIN;

      // Apply additional rotation for visual effect
      earthRef.current.rotateY(earthRotAmt.current);
    }

    // Update puzzle pieces positions and rotations from physics bodies
    puzzleRefs.forEach((ref, i) => {
      const mesh = ref.current;
      const body = puzzleBodies.current[i];
      if (mesh && body) {
        mesh.position.copy(body.position as unknown as THREE.Vector3);
        mesh.quaternion.copy(body.quaternion as unknown as THREE.Quaternion);

        // Stabilize puzzle pieces towards original positions
        const originalPos = originalPositions.current[
          i
        ] as unknown as CANNON.Vec3;
        const posDiff = new CANNON.Vec3().copy(originalPos).vsub(body.position);
        const stabilizationForce = posDiff.scale(10);
        body.applyForce(stabilizationForce, body.position);
      }

      // Handle blink effect
      if (mesh) {
        const material = mesh.material as THREE.ShaderMaterial;
        if (material.uniforms.blink.value > 0) {
          material.uniforms.blink.value = 0;
        } else {
          material.uniforms.blink.value = blinkValues.current[i];
          blinkValues.current[i] = 0;
        }
      }
    });

    // Randomly blink the puzzle pieces
    if (Math.random() < 0.01) {
      const index = Math.floor(Math.random() * 4);
      blinkValues.current[index] = 1;
    }

    // Update camera sway based on mouse movement
    swayOffset.current.x =
      (state.mouse.x * swayMag - swayOffset.current.x) * 0.5;
    swayOffset.current.y =
      (state.mouse.y * swayMag - swayOffset.current.y) * 0.5;
    camera.position.x = originalCameraPosition.current.x + swayOffset.current.x;
    camera.position.y = originalCameraPosition.current.y + swayOffset.current.y;

    // Handle zoom animation
    if (isZooming.current) {
      zoomProgress.current += delta / zoomDuration;
      const t = Math.min(zoomProgress.current, 1);
      const easedT = easeInOutQuad(t);

      // Interpolate camera position
      camera.position.lerpVectors(
        zoomStartPos.current,
        zoomEndPos.current,
        easedT
      );

      if (t >= 1) {
        isZooming.current = false;
      }
    }
  });

  // Handle 'z' key press to trigger zoom for debugging
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'z') {
        // Define desired end distance from Earth
        if (ref && typeof (ref as any).current?.zoomIntoEarth === 'function') {
          (ref as any).current.zoomIntoEarth();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [ref]);

  return (
    <group ref={group} {...props} dispose={null}>
      <mesh
        ref={earthRef}
        castShadow
        receiveShadow
        geometry={(nodes.EARTH as THREE.Mesh).geometry}
        rotation={[-0.179, 0.483, 0.03]}
        scale={1.644}
      >
        <CustomShaderMaterial
          baseMaterial={THREE.MeshStandardMaterial}
          vertexShader={earthVertexShader}
          fragmentShader={earthFragmentShader}
          uniforms={{
            fresnelPower: { value: 4.3 },
            fresnelStrength: { value: 0.37 },
          }}
          map={(materials.GOODEARTH as THREE.MeshStandardMaterial).map}
          roughnessMap={
            (materials.GOODEARTH as THREE.MeshStandardMaterial).roughnessMap
          }
          roughness={0.9}
        />
      </mesh>
      {[nodes.puzzle1, nodes.puzzle2, nodes.puzzle3, nodes.puzzle4].map(
        (node, i) => (
          <mesh
            ref={puzzleRefs[i]}
            key={i}
            castShadow
            receiveShadow
            geometry={(node as THREE.Mesh).geometry}
            position={[node.position.x, node.position.y, node.position.z]}
            rotation={[node.rotation.x, node.rotation.y, node.rotation.z]}
          >
            <CustomShaderMaterial
              baseMaterial={THREE.MeshStandardMaterial}
              vertexShader={puzzleVertexShader}
              fragmentShader={puzzleFragmentShader}
              uniforms={{
                map: {
                  value: (
                    materials[`puzz${i + 1}`] as THREE.MeshStandardMaterial
                  ).map,
                },
                col: { value: new THREE.Color(puzzleColors[i]) },
                blink: { value: 0.0 },
              }}
              roughness={0}
              metalness={0}
            />
          </mesh>
        )
      )}
    </group>
  );
});

export const IntroScreenScene = () => {
  const introScreenRef = useRef<any>(null);

  // Example of how a parent component might trigger the zoom
  // useEffect(() => {
  //   if (introScreenRef.current) {
  //     introScreenRef.current.zoomIntoEarth(0.8);
  //   }
  // }, []);

  return (
    <Canvas>
      <Suspense fallback={null}>
        <IntroScreenModel ref={introScreenRef} />
        <Environment
          files={[
            '/assets/stars/px.jpg',
            '/assets/stars/nx.jpg',
            '/assets/stars/py.jpg',
            '/assets/stars/ny.jpg',
            '/assets/stars/pz.jpg',
            '/assets/stars/nz.jpg',
          ]}
          background
          environmentIntensity={1}
        />
        <directionalLight position={[5, 5, 10]} intensity={1.5} />
        <ambientLight intensity={0.5} />
      </Suspense>
    </Canvas>
  );
};
